// npm
import React, { Component } from "react";
import { connect } from "react-redux";

// material-ui
import Button from "@material-ui/core/Button";
import withStyles from "@material-ui/core/styles/withStyles";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";

// react
import { MaterialText } from "components/presentation/material-text";

// redux (selectors)
import { getExamData, getSchedulesData } from "redux/reducers/selectors";
import { getGuid } from "redux/reducers/exam/selectors";
import { getScheduleDataByExamGuid } from "redux/reducers/schedules/selectors";
import { getProctorProvider } from "redux/reducers/schedules/schedule/selectors";

// messages
import { withMessages } from "components/hocs/messages";
import { MESSAGE_IDS, messageNotDefined } from "constants/message-ids";

import { PROCTORING_TYPES } from "constants/proctoring";

// OctoProctor keeps recording on the outro screen so the outro message is captured.
// The session is stopped when the learner presses "End Session", when the tab is
// closed, or when they leave the outro some other way (Close, resit, logout).

// How long to wait for supervisor.stop() before closing the tab anyway.
const STOP_TIMEOUT = 5000;

// These messages are new, so they will not exist on a silo that has not been
// re-provisioned.
const FALLBACKS = {
    [MESSAGE_IDS.EXAM.PROCTORING_OCTOPROCTOR_END_SESSION]: "End Session",
    [MESSAGE_IDS.EXAM.PROCTORING_OCTOPROCTOR_SESSION_ENDED]: "Session ended - you can now close this tab",
};

const msg = (messages, id) => {
    const text = messages[id];
    return messageNotDefined(text) ? FALLBACKS[id] : text;
};

// window.supervisor is set by OpenOctoProctor. Clearing it makes this safe to call
// more than once, and stops a later exam (e.g. a resit) from being stopped by this one.
const stopOctoProctor = () => {
    const supervisor = window.supervisor;
    if (!supervisor) return Promise.resolve();

    window.supervisor = null;

    try {
        console.log("Closing OctoProctor session");
        return Promise.resolve(supervisor.stop()).catch((e) =>
            console.log("Unable to close OctoProctor session", e)
        );
    } catch (e) {
        console.log("Unable to close OctoProctor session", e);
        return Promise.resolve();
    }
};

class OctoProctorEndSession extends Component {
    state = { ending: false, ended: false };

    constructor(props) {
        super(props);

        this.onEndSession = this.onEndSession.bind(this);
    }

    componentDidMount() {
        // pagehide fires on tab close, reload and navigating away from the player.
        // stop() cannot be awaited here, so this is best effort.
        window.addEventListener("pagehide", stopOctoProctor);
    }

    componentWillUnmount() {
        window.removeEventListener("pagehide", stopOctoProctor);
        stopOctoProctor();
    }

    onEndSession() {
        this.setState({ ending: true });

        const timeout = new Promise((resolve) => setTimeout(resolve, STOP_TIMEOUT));

        Promise.race([stopOctoProctor(), timeout]).then(() => {
            window.close();

            // browsers only let a script close a tab that a script opened; if the
            // learner opened the player themselves, the tab is still here.
            this.setState({ ending: false, ended: true });
        });
    }

    render() {
        const { proctoringProvider, messages, classes } = this.props;
        const { ending, ended } = this.state;

        if (proctoringProvider !== PROCTORING_TYPES.OCTO_PROCTOR) {
            return null;
        }

        if (ended) {
            return (
                <MaterialText className={classes.ended}>
                    {msg(messages, MESSAGE_IDS.EXAM.PROCTORING_OCTOPROCTOR_SESSION_ENDED)}
                </MaterialText>
            );
        }

        return (
            <div className={classes.buttons}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={this.onEndSession}
                    disabled={ending}
                >
                    {msg(messages, MESSAGE_IDS.EXAM.PROCTORING_OCTOPROCTOR_END_SESSION)}
                    <PowerSettingsNewIcon className={classes.icon} />
                </Button>
            </div>
        );
    }
}

const styles = ({ spacing, palette }) => ({
    buttons: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: spacing.unit * 2,
    },
    icon: {
        marginLeft: spacing.unit,
    },
    ended: {
        color: palette.background.contrastText,
        marginTop: spacing.unit * 2,
        fontWeight: 700,
    },
});

const mapStoreToProps = (store) => {
    const examData = getExamData(store);
    const examGuid = getGuid(examData);

    const schedulesData = getSchedulesData(store);
    const scheduleData = getScheduleDataByExamGuid(schedulesData)(examGuid);

    return { proctoringProvider: getProctorProvider(scheduleData) };
};

OctoProctorEndSession = connect(mapStoreToProps)(OctoProctorEndSession);
OctoProctorEndSession = withStyles(styles)(OctoProctorEndSession);
OctoProctorEndSession = withMessages(OctoProctorEndSession);

export { OctoProctorEndSession };
